import * as React from 'react';

import {contrastColor} from '@pinecast/common/color';
import {DEFAULT_FONT} from '@pinecast/common/constants';
import {defineMessages, FormattedMessage, useIntl} from '@pinecast/i18n';
import Pinecast from '@pinecast/common/icons/Pinecast';
import {useCSS} from '@pinecast/styles';
import {usePreviousValue} from '@pinecast/common/hooks/usePreviousValue';

import {Episode, Podcast} from './models';
import IconDownload from './icons/IconDownload';
import IconNext from './icons/IconNext';
import IconPrevious from './icons/IconPrevious';
import {MOBILE} from './mobile';
import SpeedSelector from './SpeedSelector';
import SeekBar from './SeekBar';
import PlayerIconButton from './PlayerIconButton';
import PlayPause from './PlayPause';
import TimeCode, {
  extractCurrentPosition,
  extractRemainingDuration,
} from './TimeCode';
import {useTheme} from './ThemeProvider';
import VolumeControl from './VolumeControl';
import Artwork from './Artwork';
import AudioCaptionHeader from './AudioCaptionHeader';

const messages = defineMessages({
  next: {
    id: 'pl-spa.AudioPlayer.next',
    description: 'Label for next track button',
    defaultMessage: 'Next',
  },
  previous: {
    id: 'pl-spa.AudioPlayer.previous',
    description: 'Label for previous track button',
    defaultMessage: 'Previous',
  },
  download: {
    id: 'pl-spa.AudioPlayer.download',
    description: 'Label for download button',
    defaultMessage: 'Download episode',
  },

  cobranding: {
    id: 'pl-spa.AudioPlayer.cobranding',
    description: 'Pinecast plug',
    defaultMessage: 'This podcast is hosted for free on <Link>Pinecast</Link>',
  },
});

type Props = {
  episode: Episode;
  podcast: Podcast;
  hasNext: boolean;
  hasPrevious: boolean;
  onNext: () => void;
  onPrevious: () => void;
};

const AudioPlayer = ({
  episode,
  hasNext,
  hasPrevious,
  onNext,
  onPrevious,
  podcast,
}: Props) => {
  const css = useCSS();
  const intl = useIntl();

  const theme = useTheme();

  const [audio, setAudio] = React.useState<HTMLAudioElement>(() => new Audio());

  const previousEpisode = usePreviousValue(episode);
  React.useEffect(() => {
    if (!audio.paused) {
      audio.currentTime = 0;
      audio.pause();
    }
    const newAudio = new Audio();
    newAudio.loop = audio.loop;
    newAudio.playbackRate = audio.playbackRate;
    setAudio(newAudio);
    if (previousEpisode && previousEpisode !== episode) {
      const playbackRate = audio.playbackRate;
      const loop = audio.loop;
      newAudio.src = episode.audio;
      newAudio.playbackRate = playbackRate;
      newAudio.loop = loop;
      newAudio.play();
    }

    const autoPlay = () => {
      if (hasNext) {
        onNext();
      }
    };
    newAudio.addEventListener('ended', autoPlay);

    return () => {
      newAudio.removeEventListener('ended', autoPlay);
    };
  }, [episode.audio]); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerLoad = React.useCallback(() => {
    if (!audio.src) {
      const playbackRate = audio.playbackRate;
      const loop = audio.loop;
      audio.src = episode.audio;
      audio.playbackRate = playbackRate;
      audio.loop = loop;
    }
  }, [audio, episode.audio]);

  const onPlay = () => {
    triggerLoad();
    audio.play();
  };
  const onPause = () => {
    triggerLoad();
    audio.pause();
  };

  return (
    <div
      className={css({
        background: theme.primaryColor,
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        color: contrastColor(theme.primaryColor),
        display: 'grid',
        flex: '0 0',
        fontFamily: DEFAULT_FONT,
        gridTemplateColumns: '150px 1fr',
        gap: '12px',
        padding: '6px',
        transition: 'gap 0.2s',
        [MOBILE]: {
          gap: '8px',
          gridTemplateColumns: '96px 1fr',
        },
      })}
    >
      <Artwork
        src={episode.artwork || podcast.artwork}
        podcast={podcast}
        episode={episode}
      />
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-around',
          position: 'relative',
        })}
      >
        <AudioCaptionHeader audio={audio} episode={episode} podcast={podcast} />
        {podcast.cobranding && (
          <div
            className={css({
              alignItems: 'center',
              border: '1px solid #eee',
              borderRadius: '4px',
              display: 'flex',
              fontSize: '14px',
              gap: '4px',
              marginTop: '8px',
              padding: '4px 8px',
              '@media (max-width: 500px)': {
                fontSize: '11px',
              },
            })}
          >
            <Pinecast color="#06f" size={18} />
            <span>
              <FormattedMessage
                {...messages.cobranding}
                values={{
                  Link: (...c) => (
                    <a href="https://pinecast.com/" target="_blank">
                      {c}
                    </a>
                  ),
                }}
              />
            </span>
          </div>
        )}
        {/* {episode.subtitle && (
          <strong className={css({fontWeight: 500})}>{episode.subtitle}</strong>
        )} */}
      </div>
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          justifyContent: 'space-around',
        })}
      >
        <div
          className={css({
            alignItems: 'center',
            display: 'grid',
            gridTemplateColumns: 'min-content 1fr 80px',
          })}
        >
          <PlayPause audio={audio} onPlay={onPlay} onPause={onPause} />
          <SeekBar
            audio={audio}
            providedDuration={episode.audioDuration}
            triggerLoad={triggerLoad}
          />
          <VolumeControl audio={audio} />
        </div>
        <div
          className={css({
            alignItems: 'center',
            display: 'flex',
            gap: '8px',
          })}
        >
          {hasPrevious && (
            <PlayerIconButton
              label={intl.formatMessage(messages.previous)}
              onClick={onPrevious}
            >
              <IconPrevious size={12} />
            </PlayerIconButton>
          )}
          {hasNext && (
            <PlayerIconButton
              label={intl.formatMessage(messages.next)}
              onClick={onNext}
            >
              <IconNext size={12} />
            </PlayerIconButton>
          )}
          <SpeedSelector audio={audio} />
          {episode.audioDownload && (
            <PlayerIconButton
              label={intl.formatMessage(messages.download)}
              onClick={() => window.location.assign(episode.audioDownload!)}
            >
              <IconDownload size={16} />
            </PlayerIconButton>
          )}
          <TimeCode
            audio={audio}
            extractor={extractCurrentPosition}
            providedDuration={episode.audioDuration}
          />
          <TimeCode
            audio={audio}
            extractor={extractRemainingDuration}
            providedDuration={episode.audioDuration}
          />
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;
